import { useDeleteUniversityMutation } from "@api/university";
import { ModalVariant, useModal } from "@components/Modal";
import { useToastEffect } from "@hooks/hooks";
import { PatchUniversityDTO } from "@onetutor/shared/dto";
import { FormEvent, useId } from "react";
import { useTranslation } from "react-i18next";

interface UniversityDetailsFormProps {
  university: PatchUniversityDTO;
  universityId?: string;
  isCreating: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onUniversityChange: (
    field: keyof PatchUniversityDTO,
    value: string | number | null,
  ) => void;
  onDeleteSuccess?: () => void;
}

const UniversityDetailsForm = ({
  university,
  universityId,
  isCreating,
  onSubmit,
  onUniversityChange,
  onDeleteSuccess,
}: UniversityDetailsFormProps) => {
  const { t } = useTranslation(["universityManagement", "commons"]);

  const fullNameInputId = useId();
  const shortNameInputId = useId();
  const colorInputId = useId();
  const maxCourseCountInputId = useId();
  const instanceUrlInputId = useId();
  const idpIdInputId = useId();

  const { Modal: DeleteModal, openModal: openDeleteModal } = useModal();

  const [
    deleteUniversity,
    { isSuccess: isSuccessDelete, isError: isErrorDelete },
  ] = useDeleteUniversityMutation();

  useToastEffect({
    isSuccess: isSuccessDelete,
    successMessage: t("universityManagementPage.successDeleteUniversity", {
      ns: "toast",
    }),
    isError: isErrorDelete,
  });

  const handleDelete = async () => {
    if (universityId) {
      await deleteUniversity(universityId);
      onDeleteSuccess?.();
    }
  };

  return (
    <div className="card bg-base-200 shadow-lg">
      <div className="card-body">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="card-title">
            {isCreating ? t("newUniversityTitle") : t("universityDetails")}
          </h2>
          {!isCreating && (
            <button
              type="button"
              className="btn btn-error btn-sm"
              onClick={openDeleteModal}
            >
              {t("deleteUniversity")}
            </button>
          )}
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={fullNameInputId}>
                <span className="font-medium">{t("fullName")}</span>
              </label>
              <input
                id={fullNameInputId}
                type="text"
                className="input w-full"
                value={university.fullName || ""}
                onChange={(e) => onUniversityChange("fullName", e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor={shortNameInputId}>
                <span className="font-medium">{t("shortName")}</span>
              </label>
              <input
                id={shortNameInputId}
                type="text"
                className="input w-full"
                value={university.shortName || ""}
                onChange={(e) =>
                  onUniversityChange("shortName", e.target.value)
                }
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={colorInputId}>
                <span className="font-medium">{t("color")}</span>
              </label>
              <input
                id={colorInputId}
                type="color"
                className="input w-full cursor-pointer p-0"
                value={university.color || "#0066cc"}
                onChange={(e) => onUniversityChange("color", e.target.value)}
              />
            </div>
            <div>
              <label htmlFor={maxCourseCountInputId}>
                <span className="font-medium">{t("maxCourseCount")}</span>
              </label>
              <input
                id={maxCourseCountInputId}
                type="number"
                className="input w-full"
                value={university.maxCourseCount || 20}
                onChange={(e) =>
                  onUniversityChange(
                    "maxCourseCount",
                    Number.parseInt(e.target.value, 10),
                  )
                }
                min={1}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={instanceUrlInputId}>
                <span className="font-medium">{t("instanceUrl")}</span>
              </label>
              <input
                id={instanceUrlInputId}
                type="url"
                className="input w-full"
                value={university.instanceUrl || ""}
                onChange={(e) =>
                  onUniversityChange("instanceUrl", e.target.value)
                }
                placeholder="https://app.onetutor.ai"
                pattern="https://.*"
                title={t("httpsValidationTitle")}
              />
            </div>
            <div>
              <label htmlFor={idpIdInputId}>
                <span className="font-medium">{t("idpId")}</span>
              </label>
              <input
                id={idpIdInputId}
                type="text"
                className="input w-full"
                value={university.idpId || ""}
                onChange={(e) => onUniversityChange("idpId", e.target.value)}
                minLength={3}
              />
            </div>
          </div>
          <div className="pt-4">
            <button type="submit" className="btn btn-primary w-full">
              {isCreating ? t("createUniversity") : t("updateUniversity")}
            </button>
          </div>
        </form>
      </div>

      <DeleteModal
        confirmVariant={ModalVariant.DESTRUCTIVE}
        title={t("confirmDelete")}
        onConfirm={handleDelete}
        onConfirmLabel={t("delete", { ns: "commons" })}
        onCancelLabel={t("cancel", { ns: "commons" })}
      >
        <p>{t("confirmDeleteMessage")}</p>
      </DeleteModal>
    </div>
  );
};

export default UniversityDetailsForm;
